import { useMemo } from "react";

/**
 * Encapsulates the client-side quote price calculation. Mirrors backend logic.
 */
export function useQuoteCalculation({ tab, width, height, coverName, legName, handleName, extras, types, furnitureId, variation, covers, legs, handles, pricing }) {
  return useMemo(() => {
    const cover = covers.find((c) => c.name === coverName);
    const leg = legs.find((l) => l.name === legName);
    const handle = handles.find((h) => h.name === handleName);
    const coverMult = tab === "standard" ? 1 : cover?.multiplier || 1;
    const legExtra = leg?.extra_price || 0;
    const handleExtra = handle?.extra_price || 0;
    const legMode = leg?.price_mode || "per_m2";
    const handleMode = handle?.price_mode || "per_m2";

    const currentFt = types.find((t) => t.id === furnitureId);
    const mode = currentFt?.dimension_mode || "wh";

    // Effective dimensions: use defaults when not part of mode
    const effW = mode.includes("w") ? Math.max(width, 1) : Math.max(Number(currentFt?.default_width_cm) || width || 1, 1);
    const effH = mode.includes("h") ? Math.max(height, 1) : Math.max(Number(currentFt?.default_height_cm) || height || 1, 1);

    const area = (effW * effH) / 10000;
    const unit = tab === "professional" ? pricing.unit_price_professional : pricing.unit_price_standard;
    const base = area * unit;
    const withCover = base * coverMult;
    const areaUnits = area > 0 ? Math.ceil(area) : 0;
    const applyMode = (price, mode) => price * (mode === "per_m2" ? areaUnits : 1);

    const varObj = (currentFt?.variations || []).find((v) => (typeof v === "string" ? v : v?.name) === variation);
    const variationExtra = typeof varObj === "object"
      ? applyMode(Number(varObj?.extra_price || 0), varObj?.price_mode || "flat")
      : 0;

    // Extras: recompute from furniture type so mode is authoritative
    const extraFieldMap = Object.fromEntries((currentFt?.extra_fields || []).map((f) => [f.key, f]));
    const extrasSum = Object.entries(extras || {}).reduce((s, [key, val]) => {
      const picked = typeof val === "object" ? val?.name : val;
      const field = extraFieldMap[key];
      if (!picked || !field) return s;
      const opt = (field.options || []).find((o) => (typeof o === "string" ? o : o.name) === picked);
      if (!opt || typeof opt === "string") return s;
      return s + applyMode(Number(opt.extra_price || 0), opt.price_mode || "flat");
    }, 0);
    const extrasTotal = variationExtra + extrasSum;
    const legTotal = legExtra * (legMode === "per_m2" ? areaUnits : 1);
    const handleTotal = handleExtra * (handleMode === "per_m2" ? areaUnits : 1);
    const total = withCover + legTotal + handleTotal + extrasTotal;

    return { area, base, total, unit, coverMult, legExtra, legMode, legTotal, handleExtra, handleMode, handleTotal, areaUnits, extrasTotal, variationExtra, mode, effW, effH };
  }, [width, height, coverName, legName, handleName, tab, covers, legs, handles, pricing, extras, types, furnitureId, variation]);
}
